import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

const ALIASES=Object.freeze({
  revenue:['RevenueFromContractWithCustomerExcludingAssessedTax','Revenues','SalesRevenueNet'],
  netIncome:['NetIncomeLoss','ProfitLoss'],
  operatingIncome:['OperatingIncomeLoss'],
  assets:['Assets'],
  liabilities:['Liabilities'],
  equity:['StockholdersEquity','StockholdersEquityIncludingPortionAttributableToNoncontrollingInterest'],
  cash:['CashAndCashEquivalentsAtCarryingValue','CashCashEquivalentsRestrictedCashAndRestrictedCashEquivalents'],
  debtCurrent:['ShortTermBorrowings','ShortTermDebtCurrent','LongTermDebtCurrent'],
  debtNonCurrent:['LongTermDebtNoncurrent','LongTermDebt'],
  operatingCashFlow:['NetCashProvidedByUsedInOperatingActivities'],
  capex:['PaymentsToAcquirePropertyPlantAndEquipment'],
  sharesDiluted:['WeightedAverageNumberOfDilutedSharesOutstanding'],
  epsDiluted:['EarningsPerShareDiluted']
});

function chooseRows(facts,names){
  for(const name of names){
    const rows=facts&&facts[name];
    if(Array.isArray(rows)&&rows.length) return {name,rows};
  }
  return null;
}

function rowDate(r){ return r.end||r.filed||''; }

function latestAnnual(rows){
  const candidates=(rows||[]).filter(r=>
    ['10-K','20-F','40-F'].includes(r.form) ||
    r.fp==='FY'
  );
  return candidates.sort((a,b)=>
    rowDate(b).localeCompare(rowDate(a)) ||
    String(b.filed||'').localeCompare(String(a.filed||''))
  )[0]||null;
}

function latestQuarterly(rows){
  const candidates=(rows||[]).filter(r=>
    ['10-Q','6-K'].includes(r.form) ||
    ['Q1','Q2','Q3'].includes(r.fp)
  );
  return candidates.sort((a,b)=>
    rowDate(b).localeCompare(rowDate(a)) ||
    String(b.filed||'').localeCompare(String(a.filed||''))
  )[0]||null;
}

function metric(facts,aliasKey){
  const found=chooseRows(facts,ALIASES[aliasKey]||[]);
  if(!found) return {value:null,status:'MISSING',sourceFact:null,row:null};
  const row=latestAnnual(found.rows)||latestQuarterly(found.rows)||found.rows.slice().sort((a,b)=>rowDate(b).localeCompare(rowDate(a)))[0]||null;
  return row?{
    value:row.value??null,
    status:row.value==null?'MISSING':'VERIFIED',
    sourceFact:found.name,
    row
  }:{value:null,status:'MISSING',sourceFact:found.name,row:null};
}

function buildSnapshot(companyFacts={}){
  const facts=companyFacts.facts||{};
  const keys=Object.keys(ALIASES);
  const metrics={};
  for(const key of keys) metrics[key]=metric(facts,key);

  const debtVals=[metrics.debtCurrent.value,metrics.debtNonCurrent.value].filter(v=>v!==null&&v!==undefined&&v!==''&&Number.isFinite(Number(v)));
  const totalDebt=debtVals.length?debtVals.reduce((a,b)=>Number(a)+Number(b),0):null;

  const snapshot={
    version:VERSION,
    entityName:companyFacts.entityName||null,
    cik:companyFacts.cik||null,
    symbol:companyFacts.symbol||null,
    metrics,
    derived:{
      totalDebt:{
        value:totalDebt,
        status:totalDebt==null?'MISSING':'DERIVED_VERIFIED',
        inputs:['debtCurrent','debtNonCurrent']
      }
    },
    coverage:{
      available:keys.filter(k=>metrics[k].status==='VERIFIED').length,
      total:keys.length
    }
  };
  return Object.freeze(snapshot);
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const facts={
    entityName:'AAA Corp',cik:123,symbol:'AAA',
    facts:{
      Revenues:[
        {value:80,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'},
        {value:25,end:'2026-03-31',filed:'2026-05-01',form:'10-Q',fy:2026,fp:'Q1'}
      ],
      NetIncomeLoss:[
        {value:8,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      Assets:[
        {value:200,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      Liabilities:[
        {value:90,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      StockholdersEquity:[
        {value:110,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      CashAndCashEquivalentsAtCarryingValue:[
        {value:40,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      LongTermDebtNoncurrent:[
        {value:30,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      ShortTermDebtCurrent:[
        {value:5,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      NetCashProvidedByUsedInOperatingActivities:[
        {value:15,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      PaymentsToAcquirePropertyPlantAndEquipment:[
        {value:4,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      WeightedAverageNumberOfDilutedSharesOutstanding:[
        {value:10,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ],
      EarningsPerShareDiluted:[
        {value:0.8,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fy:2025,fp:'FY'}
      ]
    }
  };

  const s=buildSnapshot(facts);
  ok('entity preserved',s.entityName==='AAA Corp');
  ok('symbol preserved',s.symbol==='AAA');
  ok('revenue annual preferred',s.metrics.revenue.value===80);
  ok('revenue source fact recorded',s.metrics.revenue.sourceFact==='Revenues');
  ok('net income mapped',s.metrics.netIncome.value===8);
  ok('assets mapped',s.metrics.assets.value===200);
  ok('equity mapped',s.metrics.equity.value===110);
  ok('cash mapped',s.metrics.cash.value===40);
  ok('operating cash flow mapped',s.metrics.operatingCashFlow.value===15);
  ok('capex mapped',s.metrics.capex.value===4);
  ok('diluted shares mapped',s.metrics.sharesDiluted.value===10);
  ok('diluted eps mapped',s.metrics.epsDiluted.value===0.8);
  ok('total debt derived',s.derived.totalDebt.value===35);
  ok('derived debt identifies inputs',s.derived.totalDebt.inputs.length===2);
  ok('missing metric explicit',s.metrics.operatingIncome.status==='MISSING');
  ok('coverage does not count missing',s.coverage.available<s.coverage.total);

  const alt=buildSnapshot({facts:{
    RevenueFromContractWithCustomerExcludingAssessedTax:[
      {value:100,end:'2025-12-31',filed:'2026-02-01',form:'10-K',fp:'FY'}
    ]
  }});
  ok('preferred revenue alias supported',alt.metrics.revenue.value===100);

  const quarterlyOnly=metric({
    Revenues:[
      {value:22,end:'2026-03-31',filed:'2026-05-01',form:'10-Q',fp:'Q1'}
    ]
  },'revenue');
  ok('quarterly fallback supported',quarterlyOnly.value===22);

  const missing=buildSnapshot({facts:{}});
  ok('empty snapshot keeps missing',missing.metrics.revenue.status==='MISSING');
  ok('empty debt not falsely zero',missing.derived.totalDebt.value===null);

  const api={VERSION,buildSnapshot,metric,latestAnnual,latestQuarterly};
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestSECCanonicalFinancialSnapshot=Object.freeze({
  VERSION,ALIASES,chooseRows,latestAnnual,latestQuarterly,metric,buildSnapshot,selfTest
});
})(engineContext);